import React, { useEffect, useState } from "react";
import Modal from "@mui/material/Modal";
import { RxCross1 } from "react-icons/rx";
import { useFormik } from "formik";
import { TextField } from "@mui/material";
import * as Yup from "yup";
import SelectInput from "../../SelectInput/SelectInput";
import { HttpService } from "../../../service/HttpService";
import { useAuth } from "../../../context/AuthProvider";
import { Tab, Tabs } from "@mui/material";
import { toast } from "react-toastify";
import { calculateNutrientColumn } from "../../../utils/nutrientCalculator";

const growthPhaseToColumnMap = {
  1: "VEG 1",
  2: "VEG 2",
  3: "VEG 3",
  4: "BLOOM 1",
  5: "BLOOM 2",
  6: "BLOOM 3",
  7: "BLOOM 4",
  8: "BLOOM 5",
  9: "BLOOM 6",
  10: "BLOOM 7",
  11: "BLOOM 8",
};

const AddbackModal = ({
  open,
  onClose,
  onSave,
  scheduleTaskId,
  taskId,
  rowData,
}) => {
  const { setShowLoginPopup } = useAuth();
  const httpService = new HttpService(setShowLoginPopup);
  const [units, setUnits] = useState([]);
  const [epicenterTab, setEpicenterTab] = useState(0);
  const [tabDrafts, setTabDrafts] = useState({
    E1: null,
    E2: null,
    E3: null,
  });
  const [showNutrients, setShowNutrients] = useState(false);

  const getTabKey = (tabIndex) => {
    if (tabIndex === 0) return "E1";
    if (tabIndex === 1) return "E2";
    return "E3";
  };

  const formik = useFormik({
    initialValues: {
      unit: "",
      epicenter: 0,
      total_system_volume: "",
      ppm: "",
      nutrients: {}, // ✅ ADD THIS
    },

    validationSchema: Yup.object({
      unit: Yup.string().trim(),
      epicenter: Yup.number().min(0).max(2),
      total_system_volume: Yup.string().trim(),
      ppm: Yup.string().trim(),
    }),
    enableReinitialize: false,
    validateOnChange: true,
    onSubmit: async (values) => {
      const epicenterKeys = ["E1", "E2", "E3"];

      const currentKey = getTabKey(epicenterTab);
      const mergedDrafts = {
        ...tabDrafts,
        [currentKey]: {
          unit: values.unit || "",
          epicenter: values.epicenter ?? epicenterTab,
          total_system_volume: values.total_system_volume || "",
          ppm: values.ppm || "",
          nutrients: tabDrafts[currentKey]?.nutrients || {}, // ✅ ADD
          add_back_id: tabDrafts[currentKey]?.add_back_id || null,
        },
      };
      const currentDraft = mergedDrafts[currentKey];
      if (!currentDraft || !currentDraft.unit) {
        formik.setFieldTouched("unit", true, false);
        formik.setFieldError("unit", "Growth Phase is required");
        return;
      }
      const selections = epicenterKeys
        .map((key, index) => {
          const draft = mergedDrafts[key];
          if (!draft || !draft.unit) return null;

          // Find existing add_back_id from rowData
          const existingAddBack = rowData?.task_add_backs?.find(
            (swap) => swap.epicenter === key
          );

          return {
            epicenter: key,
            growth_phase_id: draft.unit === "select" ? null : draft.unit,
            total_system_volume: draft.total_system_volume || "",
            ppm: draft.ppm || "",
            nutrients: draft.nutrients || {}, // ✅ ADD THIS
            add_back_id: existingAddBack?.add_back_id || null,
            tabIndex: index,
          };
        })
        .filter(Boolean);

      try {
        // const responses = await Promise.all(
        //   selections.map((sel) =>
        //     httpService.post("task/assign-full-swap", {
        //       task_id: taskId,
        //       growth_phase_id: sel.growth_phase_id,
        //       epicenter: sel.epicenter,
        //     })
        //   )
        // );
        const addBacks = selections.map((sel) => ({
          schedule_task_id: scheduleTaskId,
          growth_phase_id: sel.growth_phase_id,
          epicenter: sel.epicenter,
          total_system_volume: sel.total_system_volume,
          ppm: sel.ppm,
          nutrients: sel.nutrients,
          add_back_id: sel.add_back_id,
        }));

        const responses = await httpService.post("task/assign-add-back", {
          addBacks: addBacks,
        });

        if (responses && responses.success) {
          toast.success("Add back assigned successfully", {
            position: "top-right",
          });
          if (onSave) {
            onSave({
              task_id: scheduleTaskId,
              selections,
            });
          }
          formik.resetForm();
          setTabDrafts({
            E1: null,
            E2: null,
            E3: null,
          });
          if (onClose) onClose();
        }
      } catch (error) {
        console.error("Error assigning full swap:", error);
        toast.error(
          error?.response?.data?.error ||
            error?.response?.data?.message ||
            "Failed to assign full swap",
          {
            position: "top-right",
          }
        );
      }
    },
  });

  const fetchRecipe = () => {
    httpService
      // .get("full-swap/get-full-swap")
      .get("growth-phase/get-growth-phase")
      // .get("add-back/get-add-back")
      .then((response) => {
        const list = response?.data || [];
        const mapped = list.map((item) => ({
          id: item.id,
          name: item.name,
        }));
        setUnits(mapped);
      })
      .catch((error) => {
        console.error("Error fetching recipes for Full Swap:", error);
      });
  };

  useEffect(() => {
    if (open) {
      fetchRecipe();
      setEpicenterTab(0);

      const existingSwaps = rowData?.task_add_backs || [];
      const initialDrafts = {
        E1: { unit: "select", epicenter: 0 },
        E2: { unit: "select", epicenter: 1 },
        E3: { unit: "select", epicenter: 2 },
      };

      existingSwaps.forEach((swap) => {
        initialDrafts[swap.epicenter] = {
          unit: swap.growth_phase_id ?? "select",
          epicenter:
            swap.epicenter === "E1" ? 0 : swap.epicenter === "E2" ? 1 : 2,
          total_system_volume: swap.total_system_volume || "",
          ppm: swap.ppm || "",
          nutrients: swap.nutrients || {},
          add_back_id: swap.add_back_id,
        };
      });
      setTabDrafts(initialDrafts);

      const firstTab = initialDrafts.E1;
      formik.resetForm({
        values: {
          unit: firstTab?.unit || "",
          epicenter: firstTab?.epicenter ?? 0,
          total_system_volume: firstTab?.total_system_volume || "",
          ppm: firstTab?.ppm || "",
          nutrients: firstTab?.nutrients || {},
        },
      });

      if (Object.keys(firstTab?.nutrients || {}).length > 0) {
        setShowNutrients(true);
      }
    }
  }, [open, rowData]);

  // useEffect(() => {
  //   const { unit, total_system_volume, ppm } = formik.values;

  //   // ❌ do nothing until growth phase selected
  //   if (!unit) {
  //     formik.setFieldValue("nutrients", {}, false);
  //     return;
  //   }

  //   // ❌ do nothing until both values entered
  //   if (!total_system_volume || !ppm) {
  //     formik.setFieldValue("nutrients", {}, false);
  //     return;
  //   }

  //   const columnName = growthPhaseToColumnMap[unit];
  //   if (!columnName) return;

  //   const calculatedNutrients = calculateNutrientColumn(
  //     Number(total_system_volume),
  //     Number(ppm),
  //     columnName
  //   );

  //   // ✅ STORE OBJECT IN FORMIK
  //   formik.setFieldValue("nutrients", calculatedNutrients, false);
  // }, [formik.values.unit, formik.values.total_system_volume, formik.values.ppm]);

  // useEffect(() => {
  //   // Reset preview when inputs change
  //   setShowNutrients(false);
  //   formik.setFieldValue("nutrients", {}, false);
  // }, [formik.values.unit, formik.values.total_system_volume, formik.values.ppm]);

  const handleClose = () => {
    formik.resetForm();
    setTabDrafts({
      E1: null,
      E2: null,
      E3: null,
    });
    if (onClose) onClose();
  };

  return (
    <Modal
      className="department-modal"
      open={open}
      onClose={handleClose}
      aria-labelledby="fullswap-modal-title"
      aria-describedby="fullswap-modal-description"
    >
      <form onSubmit={formik.handleSubmit}>
        <div className="modal_block add-back-modal">
          <div className="modal_header">
            <div className="modal_title_wrapper">
              <h2 className="modal_title">Add Back</h2>
            </div>
            <div className="cancel-btn-wrap">
              <RxCross1 onClick={handleClose} />
            </div>
          </div>
          <div className="modal_content">
            <Tabs
              variant="scrollable"
              value={epicenterTab}
              onChange={(e, newValue) => {
                const currentKey = getTabKey(epicenterTab);
                const currentValues = formik.values;

                setTabDrafts((prev) => {
                  const updated = {
                    ...prev,
                    [currentKey]: {
                      unit: currentValues.unit || "",
                      epicenter: currentValues.epicenter ?? epicenterTab,
                      total_system_volume:
                        currentValues.total_system_volume || "",
                      ppm: currentValues.ppm || "",
                      nutrients: currentValues.nutrients || {},
                      add_back_id: prev[currentKey]?.add_back_id || null,
                    },
                  };

                  const newKey = getTabKey(newValue);
                  const newDraft = updated[newKey];

                  if (newDraft) {
                    formik.setValues({
                      unit: newDraft.unit || "",
                      epicenter: newDraft.epicenter ?? newValue,
                      total_system_volume: newDraft.total_system_volume || "",
                      ppm: newDraft.ppm || "",
                      nutrients: newDraft.nutrients || {},
                    });

                    setShowNutrients(
                      newDraft.nutrients &&
                        Object.keys(newDraft.nutrients).length > 0
                    );
                  } else {
                    formik.setValues({
                      unit: "",
                      epicenter: newValue,
                      total_system_volume: "",
                      ppm: "",
                      nutrients: {},
                    });
                    setShowNutrients(false);
                  }

                  return updated;
                });

                setEpicenterTab(newValue);
              }}
              sx={{
                backgroundColor: "#fffffc",
                "& .MuiTabs-indicator": {
                  backgroundColor: "#00A272",
                  height: "3px",
                },
                "& .MuiTab-root": {
                  position: "relative",
                  color: "#A1A2A1",
                  fontWeight: 400,
                  fontSize: "16px",
                  textTransform: "none",
                  padding: "12px 20px",
                  marginRight: "8px",
                  "&:not(.Mui-selected)::after": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: "3px",
                    backgroundColor: "#C4C4C4",
                  },
                  "&.Mui-selected": {
                    color: "#00A272 !important",
                    fontWeight: 600,
                    backgroundColor: "#E6F6F1",
                    borderRadius: "4px 4px 0 0",
                  },
                },
              }}
            >
              <Tab label="Epicenter 1" />
              <Tab label="Epicenter 2" />
              <Tab label="Epicenter 3" />
            </Tabs>

            <div className="row align-items-center">
              <div className="col-md-3 mt-sm-0 mt-3">
                <div className="form_field_wrapper mb-0">
                  <label className="mt-2">
                    Growth Phase
                    <span style={{ color: "red" }}>*</span>
                  </label>
                </div>
              </div>
              <div className="col-md-9 ps-0">
                <div className="form_field_wrapper">
                  <SelectInput
                    name="unit"
                    label=""
                    required={false}
                    value={formik.values.unit || ""}
                    options={units}
                    multiple={false}
                    onChange={(event) => {
                      formik.setFieldValue("unit", event.target.value);
                    }}
                    onBlur={formik.handleBlur}
                    error={formik.touched.unit && formik.errors.unit}
                    displayEmpty
                    enableSelect={false}
                  />
                </div>
              </div>
              <div className="col-md-12"></div>
            </div>

            <div className="row align-items-center">
              <div className="col-md-6 col-6">
                <div className="row align-items-center">
                  <div className="col-md-8 pe-0">
                    <div className="form_field_wrapper mb-0">
                      <label className="mb-2">
                        Total System Volume
                        <span style={{ color: "red" }}>*</span>
                      </label>
                    </div>
                  </div>
                  <div className="col-md-4 ps-0">
                    <div className="form_field_wrapper">
                      <TextField
                        type="number"
                        name="total_system_volume"
                        inputMode="numeric"
                        // label="Total System Volume"
                        fullWidth
                        size="small"
                        margin="dense"
                        value={formik.values.total_system_volume || ""}
                        disabled={
                          !formik.values.unit || formik.values.unit === "select"
                        }
                        onChange={(e) => {
                          formik.handleChange(e);
                          setShowNutrients(false);
                          formik.setFieldValue("nutrients", {}, false);
                        }}
                        onBlur={formik.handleBlur}
                        // placeholder="Enter Total System Volume"
                        error={Boolean(
                          formik.touched.total_system_volume &&
                            formik.errors.total_system_volume
                        )}
                        helperText={
                          formik.touched.total_system_volume &&
                          formik.errors.total_system_volume
                        }
                      />
                      {formik.touched.total_system_volume &&
                        formik.errors.total_system_volume && (
                          <div className="invalid-feedback">
                            {formik.errors.total_system_volume}
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-6 justify-content-end">
                <div className="row align-items-center">
                  <div className="col-md-4 text-md-end">
                    <div className="form_field_wrapper mb-0">
                      <label>
                        PPM
                        <span style={{ color: "red" }}>*</span>
                      </label>
                    </div>
                  </div>
                  <div className="col-md-8">
                    <div className="form_field_wrapper">
                      <TextField
                        type="number"
                        name="ppm"
                        inputMode="numeric"
                        // label="PPM"
                        fullWidth
                        size="small"
                        margin="dense"
                        value={formik.values.ppm || ""}
                        disabled={
                          !formik.values.unit || formik.values.unit === "select"
                        }
                        onChange={(e) => {
                          formik.handleChange(e);
                          setShowNutrients(false);
                          formik.setFieldValue("nutrients", {}, false);
                        }}
                        onBlur={formik.handleBlur}
                        // placeholder="Enter PPM"
                        error={Boolean(formik.touched.ppm && formik.errors.ppm)}
                        helperText={formik.touched.ppm && formik.errors.ppm}
                      />
                      {formik.touched.ppm && formik.errors.ppm && (
                        <div className="invalid-feedback">
                          {formik.errors.ppm}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-12 mb-2 text-end">
                <button
                  type="button"
                  className="btn btn-border show-btn"
                  onClick={() => {
                    const { unit, total_system_volume, ppm } = formik.values;

                    if (!unit || !total_system_volume || !ppm) {
                      toast.error(
                        "Please select Growth Phase and enter values for Total System Volume and PPM"
                      );
                      return;
                    }

                    const columnName = growthPhaseToColumnMap[unit];
                    if (!columnName) return;

                    const calculatedNutrients = calculateNutrientColumn(
                      Number(total_system_volume),
                      Number(ppm),
                      columnName
                    );

                    let normalizedNutrients = {};

                    if (Array.isArray(calculatedNutrients)) {
                      calculatedNutrients.forEach((item) => {
                        if (item?.name && item?.value != null) {
                          normalizedNutrients[item.name] = item.value;
                        }
                      });
                    } else if (typeof calculatedNutrients === "object") {
                      normalizedNutrients = calculatedNutrients;
                    }

                    formik.setFieldValue(
                      "nutrients",
                      normalizedNutrients,
                      false
                    );

                    setTabDrafts((prev) => ({
                      ...prev,
                      [getTabKey(epicenterTab)]: {
                        ...prev[getTabKey(epicenterTab)],
                        nutrients: normalizedNutrients, // ✅ SAVE PER TAB
                      },
                    }));

                    setShowNutrients(true);
                  }}
                >
                  Show
                </button>
              </div>
            </div>
            {showNutrients &&
              Object.keys(formik.values.nutrients || {}).length > 0 && (
                <div className="row mt-3">
                  <div className="col-12">
                    <div className="nutrient-preview-box">
                      <h6 className="mb-2">
                        <strong className="d-inline-block border-bottom  border-dark">
                          Nutrients
                        </strong>
                      </h6>
                      <div className="nutrient-grid">
                        {Object.entries(formik.values.nutrients)
                          .filter(
                            ([_, value]) =>
                              value !== null &&
                              value !== undefined &&
                              value !== ""
                          )
                          .map(([nutrientName, value]) => (
                            <div
                              key={nutrientName}
                              className="nutrient-grid-item"
                            >
                              <strong>{nutrientName}</strong> : {value}
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
          </div>
          <div className="modal_footer">
            <button
              type="button"
              onClick={handleClose}
              className="btn btn-border"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={(() => {
                const activeKey = getTabKey(epicenterTab);
                const keys = ["E1", "E2", "E3"];
                let hasAnyValid = false;
                let hasAnyIncomplete = false;

                keys.forEach((key) => {
                  let vals, isValid, isEmpty;

                  if (key === activeKey) {
                    vals = formik.values;
                    isValid = showNutrients;
                  } else {
                    vals = tabDrafts[key] || {};
                    isValid =
                      vals.nutrients && Object.keys(vals.nutrients).length > 0;
                  }

                  // Check if the tab is effectively "empty"
                  isEmpty =
                    (!vals.unit || vals.unit === "select") &&
                    !vals.total_system_volume &&
                    !vals.ppm;

                  if (!isEmpty) {
                    if (isValid) {
                      hasAnyValid = true;
                    } else {
                      hasAnyIncomplete = true;
                    }
                  }
                });

                // Disable if ANY tab is incomplete (dirty but not shown)
                // OR if NO tabs are valid
                return hasAnyIncomplete || !hasAnyValid;
              })()}
            >
              Assign
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default AddbackModal;
